/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Roles & Permissions Management API Service
 * CRUD endpoints for /api/v1/venta/admin/roles
 */

import { get, post, put, del } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface RoleDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  system: boolean;
  hierarchyLevel: number;
  permissions: Record<string, string[]>; // module -> [actions]
}

export interface PermissionDto {
  id: string;
  module: string;
  action: string;
  description: string;
}

export interface CreateRoleRequest {
  code: string;
  name: string;
  description?: string;
  permissionIds?: string[];
}

export interface UpdatePermissionsRequest {
  permissionIds: string[];
}

// ─── Menu Types ──────────────────────────────────────────────────────────────

export interface PermissionInfo {
  id: string;
  action: string;
  description: string;
}

export interface MenuItemDto {
  id: string;
  code: string;
  labelKey: string;
  icon: string;
  route: string | null;
  feature: string | null;
  permissions: PermissionInfo[];
}

export interface MenuTreeDto {
  id: string;
  code: string;
  labelKey: string;
  icon: string;
  section: boolean;
  items: MenuItemDto[];
}

export interface UpdateRoleMenusRequest {
  menuIds: string[];
  permissionIds: string[];
}

// ─── Service Functions ──────────────────────────────────────────────────────

const BASE = '/venta/admin/roles';

export async function listRoles(): Promise<RoleDto[]> {
  const response = await get<ApiResponse<RoleDto[]>>(BASE);
  return response.data;
}

export async function listPermissions(): Promise<PermissionDto[]> {
  const response = await get<ApiResponse<PermissionDto[]>>(`${BASE}/permissions`);
  return response.data;
}

export async function getRole(id: string): Promise<RoleDto> {
  const response = await get<ApiResponse<RoleDto>>(`${BASE}/${id}`);
  return response.data;
}

export async function createRole(data: CreateRoleRequest): Promise<RoleDto> {
  const response = await post<ApiResponse<RoleDto>>(BASE, data);
  return response.data;
}

export async function updatePermissions(id: string, data: UpdatePermissionsRequest): Promise<RoleDto> {
  const response = await put<ApiResponse<RoleDto>>(`${BASE}/${id}/permissions`, data);
  return response.data;
}

export async function deleteRole(id: string): Promise<void> {
  await del<ApiResponse<void>>(`${BASE}/${id}`);
}

export async function resetToDefault(id: string): Promise<RoleDto> {
  const response = await post<ApiResponse<RoleDto>>(`${BASE}/${id}/reset`);
  return response.data;
}

/**
 * Fetch the current user's effective permission codes.
 * Returns flat array like ["DASHBOARD:VIEW", "SALES:CREATE", "SALES:VIEW", ...]
 */
export async function getMyPermissions(): Promise<string[]> {
  const response = await get<ApiResponse<string[]>>('/venta/me/permissions');
  return response.data;
}

// ─── Menu Service Functions ──────────────────────────────────────────────────

export async function getFullMenuTree(): Promise<MenuTreeDto[]> {
  const response = await get<ApiResponse<MenuTreeDto[]>>('/venta/admin/menus');
  return response.data;
}

export async function getMyMenus(): Promise<MenuTreeDto[]> {
  const response = await get<ApiResponse<MenuTreeDto[]>>('/venta/me/menus');
  return response.data;
}

export async function updateRoleMenus(id: string, data: UpdateRoleMenusRequest): Promise<void> {
  await put<ApiResponse<void>>(`/venta/admin/roles/${id}/menus`, data);
}

export const rolesService = {
  listRoles,
  listPermissions,
  getRole,
  createRole,
  updatePermissions,
  deleteRole,
  resetToDefault,
  getMyPermissions,
  getFullMenuTree,
  getMyMenus,
  updateRoleMenus,
};
